import { useState, useRef, useCallback, useEffect } from "react";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  PictureInPicture2, RotateCcw, RotateCw, Gauge,
} from "lucide-react";

function fmt(s) {
  if (!s || !isFinite(s)) return "0:00";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(Math.floor(s % 60)).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const HIDE_MS = 3000;
const BTN = {
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 36, height: 36, borderRadius: "50%",
  border: "none", background: "none",
  color: "rgba(255,255,255,0.85)", cursor: "pointer",
  transition: "color 0.15s, background 0.15s", flexShrink: 0,
};

export default function VideoControls({ videoRef, accentColor, rgb, isFullscreen, onToggleFullscreen }) {
  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isPip, setIsPip] = useState(false);
  const [hoverPct, setHoverPct] = useState(null);
  const [hoverX, setHoverX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [showSkipIntro, setShowSkipIntro] = useState(false);

  const hideRef = useRef(null);
  const seekBarRef = useRef(null);
  const overlayRef = useRef(null);

  const resetHide = useCallback(() => {
    setVisible(true);
    clearTimeout(hideRef.current);
    const vid = videoRef.current;
    hideRef.current = setTimeout(() => {
      if (vid && !vid.paused) setVisible(false);
    }, HIDE_MS);
  }, [videoRef]);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;

    const onTimeUpdate = () => {
      setCurrentTime(vid.currentTime);
      setShowSkipIntro(vid.currentTime >= 30 && vid.currentTime < 90 && !vid.paused);
      if (vid.buffered.length) {
        setBuffered((vid.buffered.end(vid.buffered.length - 1) / (vid.duration || 1)) * 100);
      }
    };
    const onDuration = () => setDuration(vid.duration || 0);
    const onVolume = () => { setVolume(vid.volume); setMuted(vid.muted); };
    const onRate = () => setSpeed(vid.playbackRate);
    const onPause = () => { setPaused(true); setVisible(true); clearTimeout(hideRef.current); };
    const onPlay = () => { setPaused(false); resetHide(); };
    const onPipEnter = () => setIsPip(true);
    const onPipLeave = () => setIsPip(false);

    vid.addEventListener("timeupdate", onTimeUpdate);
    vid.addEventListener("durationchange", onDuration);
    vid.addEventListener("volumechange", onVolume);
    vid.addEventListener("ratechange", onRate);
    vid.addEventListener("pause", onPause);
    vid.addEventListener("play", onPlay);
    vid.addEventListener("enterpictureinpicture", onPipEnter);
    vid.addEventListener("leavepictureinpicture", onPipLeave);

    setPaused(vid.paused);
    setVolume(vid.volume);
    setMuted(vid.muted);
    setDuration(vid.duration || 0);

    return () => {
      vid.removeEventListener("timeupdate", onTimeUpdate);
      vid.removeEventListener("durationchange", onDuration);
      vid.removeEventListener("volumechange", onVolume);
      vid.removeEventListener("ratechange", onRate);
      vid.removeEventListener("pause", onPause);
      vid.removeEventListener("play", onPlay);
      vid.removeEventListener("enterpictureinpicture", onPipEnter);
      vid.removeEventListener("leavepictureinpicture", onPipLeave);
    };
  }, [videoRef, resetHide]);

  // Document-level drag tracking
  useEffect(() => {
    if (!dragging) return;
    const vid = videoRef.current;
    const onMove = (e) => {
      const bar = seekBarRef.current;
      if (!bar || !vid?.duration) return;
      const rect = bar.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      vid.currentTime = pct * vid.duration;
    };
    const onUp = () => setDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
    };
  }, [dragging, videoRef]);

  const togglePlay = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.paused ? vid.play() : vid.pause();
  }, [videoRef]);

  const skipBy = useCallback((s) => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = Math.max(0, Math.min(vid.duration || 0, vid.currentTime + s));
  }, [videoRef]);

  const seekFromBarEvent = useCallback((e) => {
    const bar = seekBarRef.current;
    const vid = videoRef.current;
    if (!bar || !vid?.duration) return;
    const rect = bar.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    vid.currentTime = pct * vid.duration;
  }, [videoRef]);

  const toggleMute = useCallback(() => {
    const vid = videoRef.current;
    if (vid) vid.muted = !vid.muted;
  }, [videoRef]);

  const changeVolume = useCallback((v) => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.volume = v;
    vid.muted = v === 0;
  }, [videoRef]);

  const setPlaySpeed = useCallback((s) => {
    const vid = videoRef.current;
    if (vid) vid.playbackRate = s;
    setShowSpeed(false);
  }, [videoRef]);

  const togglePip = useCallback(async () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture().catch(() => {});
    } else {
      await vid.requestPictureInPicture().catch(() => {});
    }
  }, [videoRef]);

  const hasPip = typeof document !== "undefined" && "pictureInPictureEnabled" in document;
  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={overlayRef}
      style={{ position: "absolute", inset: 0, zIndex: 5 }}
      onMouseMove={resetHide}
      onTouchStart={resetHide}
      onClick={(e) => {
        if (!e.target.closest("button") && !e.target.closest("input") && !e.target.closest("[data-seekbar]")) {
          togglePlay();
          resetHide();
        }
      }}
    >
      {/* Skip Intro */}
      {showSkipIntro && (
        <button
          onClick={(e) => { e.stopPropagation(); skipBy(90 - currentTime); }}
          style={{
            position: "absolute", bottom: "5.5rem", right: "1.5rem", zIndex: 10,
            padding: "0.5rem 1.1rem",
            borderRadius: "0.45rem",
            background: "rgba(8,8,12,0.9)",
            backdropFilter: "blur(12px)",
            border: `1.5px solid ${accentColor}`,
            color: "#fff",
            fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: "0.8rem",
            letterSpacing: "0.04em", cursor: "pointer",
            boxShadow: `0 0 20px ${accentColor}40`,
            transition: "box-shadow 0.2s",
          }}
        >
          Skip Intro →
        </button>
      )}

      {/* Center play/pause pulse (shows when paused) */}
      {paused && (
        <div
          style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            zIndex: 8, pointerEvents: "none",
          }}
        >
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(16px)",
            border: "2px solid rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 0 40px rgba(${rgb},0.3)`,
          }}>
            <Play style={{ width: 30, height: 30, marginLeft: 4, color: "#fff" }} strokeWidth={2} />
          </div>
        </div>
      )}

      {/* Controls overlay */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.3s ease",
        pointerEvents: visible ? "auto" : "none",
      }}>
        {/* Gradient scrim */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 160,
          background: "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)",
          pointerEvents: "none",
        }} />

        <div style={{ position: "relative", zIndex: 2, padding: "0.6rem 1rem 0.7rem" }}>
          {/* Seek bar */}
          <div
            data-seekbar="true"
            ref={seekBarRef}
            style={{
              position: "relative", height: 22, display: "flex", alignItems: "center",
              cursor: "pointer", marginBottom: "0.3rem", userSelect: "none",
            }}
            onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setDragging(true); seekFromBarEvent(e); }}
            onMouseMove={(e) => {
              const bar = seekBarRef.current;
              if (!bar || !duration) return;
              const rect = bar.getBoundingClientRect();
              const pct2 = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              setHoverPct(pct2);
              setHoverX(e.clientX - rect.left);
            }}
            onMouseLeave={() => setHoverPct(null)}
            onTouchStart={(e) => { e.stopPropagation(); setDragging(true); seekFromBarEvent(e); }}
            onClick={(e) => { e.stopPropagation(); seekFromBarEvent(e); }}
          >
            {/* Track */}
            <div style={{ position: "absolute", left: 0, right: 0, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)" }}>
              {/* Buffered */}
              <div style={{
                position: "absolute", left: 0, width: `${buffered}%`, height: "100%",
                background: "rgba(255,255,255,0.3)", borderRadius: 2, transition: "width 0.5s",
              }} />
              {/* Progress */}
              <div style={{
                position: "absolute", left: 0, width: `${pct}%`, height: "100%",
                background: accentColor, borderRadius: 2,
              }} />
              {/* Thumb */}
              <div style={{
                position: "absolute", top: "50%", left: `${pct}%`,
                transform: "translate(-50%, -50%)",
                width: 14, height: 14, borderRadius: "50%", background: "#fff",
                boxShadow: `0 0 0 3px ${accentColor}50`,
                transition: "box-shadow 0.15s",
              }} />
            </div>
            {/* Hover time tooltip */}
            {hoverPct !== null && (
              <div style={{
                position: "absolute", bottom: "calc(100% + 8px)", left: hoverX,
                transform: "translateX(-50%)", background: "rgba(0,0,0,0.9)",
                color: "#fff", fontSize: "0.7rem", fontFamily: "Outfit, sans-serif",
                fontWeight: 700, padding: "3px 7px", borderRadius: 4,
                pointerEvents: "none", whiteSpace: "nowrap",
              }}>
                {fmt(hoverPct * duration)}
              </div>
            )}
          </div>

          {/* Buttons row */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.05rem" }}>
            {/* Play/Pause */}
            <button style={BTN} onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
              {paused
                ? <Play style={{ width: 16, height: 16, marginLeft: 2 }} strokeWidth={2} />
                : <Pause style={{ width: 16, height: 16 }} strokeWidth={2} />}
            </button>

            {/* Skip -10s */}
            <button
              style={{ ...BTN, flexDirection: "column", gap: 1, width: "auto", padding: "0 7px", height: "auto", paddingTop: 4, paddingBottom: 4 }}
              onClick={(e) => { e.stopPropagation(); skipBy(-10); }}
            >
              <RotateCcw style={{ width: 13, height: 13 }} strokeWidth={2} />
              <span style={{ fontSize: "0.52rem", fontFamily: "Outfit, sans-serif", fontWeight: 800, lineHeight: 1, color: "rgba(255,255,255,0.75)" }}>10</span>
            </button>

            {/* Skip +10s */}
            <button
              style={{ ...BTN, flexDirection: "column", gap: 1, width: "auto", padding: "0 7px", height: "auto", paddingTop: 4, paddingBottom: 4 }}
              onClick={(e) => { e.stopPropagation(); skipBy(10); }}
            >
              <RotateCw style={{ width: 13, height: 13 }} strokeWidth={2} />
              <span style={{ fontSize: "0.52rem", fontFamily: "Outfit, sans-serif", fontWeight: 800, lineHeight: 1, color: "rgba(255,255,255,0.75)" }}>10</span>
            </button>

            {/* Volume */}
            <button style={BTN} onClick={(e) => { e.stopPropagation(); toggleMute(); }}>
              {muted || volume === 0
                ? <VolumeX style={{ width: 16, height: 16 }} strokeWidth={1.75} />
                : <Volume2 style={{ width: 16, height: 16 }} strokeWidth={1.75} />}
            </button>
            <input
              type="range" min="0" max="1" step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={{ width: 68, cursor: "pointer", flexShrink: 0, accentColor }}
            />

            {/* Time */}
            <span style={{
              color: "rgba(255,255,255,0.65)", fontSize: "0.7rem",
              fontFamily: "Outfit, sans-serif", fontWeight: 600,
              marginLeft: "0.5rem", whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {fmt(currentTime)} / {fmt(duration)}
            </span>

            <div style={{ flex: 1 }} />

            {/* Speed */}
            <div style={{ position: "relative" }}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowSpeed((s) => !s); }}
                style={{
                  ...BTN,
                  width: "auto", padding: "0 0.6rem", gap: "0.2rem",
                  fontSize: "0.72rem", fontFamily: "Outfit, sans-serif", fontWeight: 700,
                  color: speed !== 1 ? accentColor : "rgba(255,255,255,0.7)",
                }}
              >
                <Gauge style={{ width: 14, height: 14 }} strokeWidth={1.75} />
                {speed}×
              </button>
              {showSpeed && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute", bottom: "calc(100% + 8px)", right: 0,
                    background: "rgba(8,8,14,0.96)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "0.75rem", overflow: "hidden", minWidth: 84,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                  }}
                >
                  {SPEEDS.map((s, i) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); setPlaySpeed(s); }}
                      style={{
                        display: "block", width: "100%",
                        padding: "0.5rem 0.9rem", textAlign: "right",
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: speed === s ? 700 : 400, fontSize: "0.78rem",
                        color: speed === s ? accentColor : "rgba(255,255,255,0.65)",
                        background: speed === s ? `rgba(${rgb},0.1)` : "none",
                        border: "none",
                        borderBottom: i < SPEEDS.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                        cursor: "pointer", transition: "background 0.12s",
                      }}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* PiP */}
            {hasPip && (
              <button
                style={{ ...BTN, color: isPip ? accentColor : "rgba(255,255,255,0.7)" }}
                onClick={(e) => { e.stopPropagation(); togglePip(); }}
                title="Picture in Picture"
              >
                <PictureInPicture2 style={{ width: 16, height: 16 }} strokeWidth={1.75} />
              </button>
            )}

            {/* Fullscreen */}
            <button style={BTN} onClick={(e) => { e.stopPropagation(); onToggleFullscreen(); }}>
              {isFullscreen
                ? <Minimize style={{ width: 16, height: 16 }} strokeWidth={1.75} />
                : <Maximize style={{ width: 16, height: 16 }} strokeWidth={1.75} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
